import {
    normalizePackageItems,
    totalPackageCards,
} from "./packageItems";

const formatPromoPrice = (promoPriceCents) => {
    const value = Number(promoPriceCents) / 100;
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value);
};

const formatItemsChip = (count) => {
    const total = Number(count) || 0;
    return total === 1 ? "1 item" : `${total} itens`;
};

const viewportMaxWidth = {
    desktop: "420px",
    mobile: "360px",
};

const PreviewChips = ({ itemsTotal, hasSavings, savingsLabel, savingsPercent, size = "md" }) => {
    const countClass = size === "sm"
        ? "rounded border border-primary/50 bg-black px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary"
        : "rounded border border-primary bg-primary/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-primary";
    const savingsClass = size === "sm"
        ? "rounded border border-emerald-500/40 bg-emerald-950/40 px-2 py-0.5 text-[11px] text-emerald-300"
        : "rounded border border-emerald-500/50 bg-emerald-950/50 px-2.5 py-1 text-xs font-medium text-emerald-300";

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className={countClass}>{formatItemsChip(itemsTotal)}</span>
            {hasSavings ? (
                <span className={savingsClass}>
                    {size === "sm" ? `−${savingsLabel}` : (
                        <>
                            Economize {savingsLabel}
                            {savingsPercent ? ` · ${savingsPercent}%` : ""}
                        </>
                    )}
                </span>
            ) : null}
        </div>
    );
};

const PackagePreview = ({
    title,
    subtitle,
    items,
    gradingType,
    numberOfCards,
    promoPriceCents,
    promoDeadlineBusinessDays,
    imageUrl,
    referencePriceCents,
    savingsCents,
    savingsPercent,
    viewport = "desktop",
    surface = "vitrine",
}) => {
    const frameStyle = { maxWidth: viewportMaxWidth[viewport] || viewportMaxWidth.desktop };
    const packageItems = normalizePackageItems(
        items?.length
            ? items
            : { gradingType, numberOfCards }
    );
    const itemsTotal = totalPackageCards(packageItems) || numberOfCards || 0;
    const priceLabel = formatPromoPrice(promoPriceCents);
    const deadlineLabel = `${promoDeadlineBusinessDays} dias úteis`;
    const hasImage = Boolean(imageUrl);
    const hasSavings = Number.isFinite(Number(savingsCents)) && Number(savingsCents) > 0;
    const referenceLabel = Number.isFinite(Number(referencePriceCents))
        ? formatPromoPrice(referencePriceCents)
        : null;
    const savingsLabel = hasSavings ? formatPromoPrice(savingsCents) : null;
    const displayTitle = title || "Pacote promocional";
    const displaySubtitle = subtitle
        || (hasSavings
            ? `Economize ${savingsLabel}${savingsPercent ? ` (${savingsPercent}%)` : ""} no pacote fechado.`
            : `Pacote fechado · ${deadlineLabel}`);

    if (surface === "carrinho") {
        return (
            <div
                data-testid="package-preview-carrinho"
                className="w-full rounded-lg border border-primary/60 bg-mgs-bg p-3 text-orange-100"
                style={frameStyle}
            >
                <div className={`flex items-start gap-3 ${hasImage ? "" : "flex-col"}`}>
                    {hasImage ? (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-primary/40 bg-black">
                            <img
                                src={imageUrl}
                                alt=""
                                className="h-full w-full object-cover"
                            />
                        </div>
                    ) : (
                        <PreviewChips
                            itemsTotal={itemsTotal}
                            hasSavings={hasSavings}
                            savingsLabel={savingsLabel}
                            savingsPercent={savingsPercent}
                            size="sm"
                        />
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-primary">{displayTitle}</p>
                        <p className="text-xs text-orange-100/70">{displaySubtitle}</p>
                        <div className="mt-2 flex items-baseline justify-between gap-2">
                            <div className="flex flex-col">
                                {referenceLabel ? (
                                    <span className="text-[11px] text-orange-100/45 line-through">{referenceLabel}</span>
                                ) : null}
                                <span className="text-base font-semibold text-orange-100">{priceLabel}</span>
                            </div>
                            <span className="text-xs text-orange-100/60">{deadlineLabel}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="package-preview-vitrine"
            className="w-full overflow-hidden rounded-lg border border-primary bg-mgs-bg text-orange-100 shadow-[0_0_0_1px_rgba(213,145,6,0.15)]"
            style={frameStyle}
        >
            {hasImage ? (
                <div className="flex aspect-[4/3] items-center justify-center border-b border-primary/40 bg-black">
                    <img
                        src={imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </div>
            ) : (
                <div className="border-b border-primary/40 bg-gradient-to-br from-black via-mgs-bg to-black px-4 py-5">
                    <PreviewChips
                        itemsTotal={itemsTotal}
                        hasSavings={hasSavings}
                        savingsLabel={savingsLabel}
                        savingsPercent={savingsPercent}
                    />
                    <p className="mt-4 text-2xl font-semibold leading-tight text-primary">{displayTitle}</p>
                    <p className="mt-2 text-sm text-orange-100/75">{displaySubtitle}</p>
                </div>
            )}
            <div className="flex flex-col gap-2 p-4">
                {hasImage ? (
                    <div>
                        <h3 className="text-lg font-semibold text-primary">{displayTitle}</h3>
                        <p className="text-sm text-orange-100/80">{displaySubtitle}</p>
                    </div>
                ) : null}
                <div className="flex items-end justify-between gap-2 border-t border-primary/30 pt-3">
                    <div className="flex flex-col">
                        {referenceLabel ? (
                            <span className="text-xs text-orange-100/45 line-through">{referenceLabel}</span>
                        ) : null}
                        <span className="text-xl font-semibold text-orange-100">{priceLabel}</span>
                    </div>
                    <span className="text-xs text-orange-100/70">{deadlineLabel}</span>
                </div>
            </div>
        </div>
    );
};

export default PackagePreview;
